import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MobileService } from '@klickdata/core/mobile';
import { Resource } from '@klickdata/core/resource';
import { Filter, GlobalFilterProperty, TableFilterComponent, TableSource } from '@klickdata/core/table';
import { Observable, map, shareReplay } from 'rxjs';
import { ResourceTableBaseService } from './resource-table-base.service';

@Component({
    selector: 'app-resource-table-base',
    templateUrl: './resource-table-base.component.html',
    styleUrls: ['./resource-table-base.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ResourceTableBaseService],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class ResourceTableBaseComponent implements OnInit {
    @Input() dataSource = new TableSource<Resource>();
    @Input() matSortDirection: 'asc' | 'desc' | '' = 'desc';
    @Input() matSortActive = 'title';
    @Input() columnsData: { [key: string]: TemplateRef<any> };
    @Input() otherContext: { [key: string]: TemplateRef<any> };
    @Input() columnsKeys: any;
    @Input() tableFilterItems: Filter<any>[];
    @Input() activeFilter: GlobalFilterProperty;

    @Input() expandingTable: boolean;
    @Input() expandedFn: (resource: Resource) => boolean = (resource: Resource) => this.expandedRowId === resource.id;
    @Output() expansionToggled = new EventEmitter<Resource>();

    public showSelectionActions: boolean;
    @Input() canSelect: boolean;
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @Input() filter: TableFilterComponent;
    @Input() expandedRowId: number | null;
    @Input() pageSizeOptions = [25, 50, 100];
    @Input() filtersExpanded = false;
    GlobalFilterProperty = GlobalFilterProperty;
    public isMobile$: Observable<boolean>;
    selection = new SelectionModel<Resource>(true, []);

    constructor(
        protected resourceService: ResourceTableBaseService,
        protected mobile: MobileService,
        protected cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.dataSource.service = this.resourceService;
        this.isMobile$ = this.mobile.isMobile().pipe(shareReplay());
        this.selection.changed.pipe(map((selection) => selection.source.selected)).subscribe((selected) => {
            this.showSelectionActions = selected.length > 1;
            this.cdr.markForCheck();
        });
    }

    public get columns(): string[] {
        let columns = [];
        if (!!this.columnsData) {
            columns = Object.keys(this.columnsData);
        }
        if (this.canSelect) {
            columns.unshift('select');
        }
        return columns;
    }
    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.dataSource.filter = this.filter;
        if (this.activeFilter) {
            this.filter.setActiveFilter(this.activeFilter);
        }
        this.updateFilters();
        setTimeout(() => {
            this.filtersExpanded = this.filter.hasActiveFilters();
            this.cdr.markForCheck();
        }, 1000);
    }
    private updateFilters() {
        const appliedFilters = [];
        if (this.tableFilterItems) {
            this.tableFilterItems.forEach((filterItem) => {
                appliedFilters.push(new Filter(filterItem.property, filterItem.items));
            });
        }

        this.filter.createOrUpdateFilter(appliedFilters);
    }
    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.data.length;
        return numSelected === numRows;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    masterToggle() {
        this.isAllSelected()
            ? this.selection.clear()
            : this.dataSource.data.forEach((row) => this.selection.select(row));
    }

    /** The label for the checkbox on the passed row */
    checkboxLabel(row?: Resource): string {
        if (!row) {
            return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id + 1}`;
    }

    toggleExpand(resource: Resource) {
        if (this.expansionToggled.observed) {
            this.expansionToggled.emit(resource);
        } else {
            this.expandedRowId = this.expandedRowId === resource.id ? null : resource.id;
        }
    }
}
